import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore as ExpandMoreIcon
} from "@mui/icons-material";
import { Box, Button, Checkbox, Collapse, FormControlLabel, IconButton, Stack, Typography } from "@mui/material";
import { Search } from "components/Search/Search";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { indexNames } from "constants/reports";
import { useActionHook } from "hooks";
import { debounce } from "lodash";
import { useAccountSelector, useCorrelationMatrixSettingsSelector, usePortfolioConstructionActions } from "store";
import {
  CorrelationMatrixSettings,
  IPortfolioConstructionCorrelationItem,
  IPortfolioConstructionDealTreeItem
} from "store/types";

import { TreeItemCheckbox } from "./TreeItemCheckbox";

interface Props {
  onCancel: () => void;
  onSubmit: (data: CorrelationMatrixSettings) => void;
  investorId?: number;
}

type FilterOption = { path: string; type: string; name: string };

// Initialize state with lastCorrelationMatrix values if available
const getInitialIndices = (items: IPortfolioConstructionCorrelationItem[]) => {
  if (!items) return [];
  return items.filter((item): item is { index: string } => "index" in item).map((item) => item.index);
};

// Recursive search function to find matching items and their parent paths
const searchInTree = (
  tree: IPortfolioConstructionDealTreeItem[],
  searchQuery: string,
  parentPath = ""
): { matchingItems: IPortfolioConstructionDealTreeItem[]; pathsToExpand: string[] } => {
  const matchingItems: IPortfolioConstructionDealTreeItem[] = [];
  const pathsToExpand: string[] = [];

  if (!searchQuery.trim()) {
    return { matchingItems: tree, pathsToExpand: [] };
  }

  const query = searchQuery.toLowerCase();

  tree.forEach((item) => {
    const itemPath = parentPath ? `${parentPath}/${item.name}` : item.name;
    const itemMatches = item.name.toLowerCase().includes(query);

    if (itemMatches) {
      matchingItems.push(item);
      // If this item matches, we need to expand its parent path
      if (parentPath) {
        pathsToExpand.push(parentPath);
      }
    }

    // Search in children
    if (item.children && item.children.length > 0) {
      const childResults = searchInTree(item.children, searchQuery, itemPath);
      matchingItems.push(...childResults.matchingItems);
      pathsToExpand.push(...childResults.pathsToExpand);

      // If any child matches, expand this item
      if (childResults.matchingItems.length > 0) {
        pathsToExpand.push(itemPath);
      }
    }
  });

  return { matchingItems, pathsToExpand };
};

const getInitialFilters = ({
  items,
  dealTree
}: {
  items: IPortfolioConstructionCorrelationItem[];
  dealTree: IPortfolioConstructionDealTreeItem[];
}): FilterOption[] => {
  if (!items || !dealTree) return [];
  const filters: FilterOption[] = [];

  // Helper function to find item path in the tree
  const findItemPath = (
    tree: IPortfolioConstructionDealTreeItem[],
    targetName: string,
    targetType: string,
    parentPath = ""
  ): string | null => {
    for (const item of tree) {
      const currentPath = parentPath ? `${parentPath}/${item.name}` : item.name;

      if (item.name === targetName && item.type === targetType) {
        return currentPath;
      }

      if (item.children) {
        const foundPath = findItemPath(item.children, targetName, targetType, currentPath);
        if (foundPath) return foundPath;
      }
    }
    return null;
  };

  items.forEach((item) => {
    if ("filter" in item) {
      Object.entries(item.filter).forEach(([key, values]) => {
        if (Array.isArray(values)) {
          values.forEach((value: string) => {
            let type = "";
            if (key === "Asset Class") type = "asset_class";
            else if (key === "Sub Asset Class") type = "sub_asset_class";
            else if (key === "deal_name") type = "deal";

            // Find the hierarchical path for this item
            const path = findItemPath(dealTree, value, type);
            if (path) {
              filters.push({ path, type, name: value });
            }
          });
        }
      });
    }
  });

  return filters;
};

export const CorrelationSettingsPrompt: React.FC<Props> = ({ onCancel, onSubmit, investorId }) => {
  const { getDealTreeAction } = usePortfolioConstructionActions();
  const settings = useCorrelationMatrixSettingsSelector();
  const isFreemium = useAccountSelector()?.is_freemium || false;

  const {
    data: dealTree,
    loading: dealTreeLoading,
    action: getDealTree
  } = useActionHook(getDealTreeAction, {
    onSuccess: (res) => {
      // Set selected filters when tree is fetched
      setSelectedFilters(getInitialFilters({ items: settings.items, dealTree: res || [] }));
      setSelectedIndices(getInitialIndices(settings.items));
    }
  });

  const [selectedIndices, setSelectedIndices] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroup, setExpandedGroup] = useState<"public" | "private" | null>(null);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (!dealTree) return;

        const { pathsToExpand } = searchInTree(dealTree, query);

        // Update expanded items to show matching results
        setExpandedItems((prev) => {
          const newExpanded = new Set(prev);
          pathsToExpand.forEach((path) => newExpanded.add(path));
          return Array.from(newExpanded);
        });
      }, 300),
    [dealTree]
  );

  // Handle search query changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  // Get filtered tree based on search
  const filteredTree = useMemo(() => {
    if (!dealTree || !searchQuery.trim()) return dealTree;

    const { matchingItems } = searchInTree(dealTree, searchQuery);
    return matchingItems;
  }, [dealTree, searchQuery]);

  const handleIndexToggle = (index: string) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      // Check if adding this item would exceed the limit
      const totalAfterAdd = prev.length + selectedFilters.length + 1;
      if (isFreemium && totalAfterAdd > maxItems) {
        return prev;
      }
      return [...prev, index];
    });
  };

  const handleFilterToggle = (path: string, type: string, name: string) => {
    setSelectedFilters((prev) => {
      if (prev.some((f) => f.path === path)) {
        return prev.filter((f) => f.path !== path);
      }
      // Check if adding this item would exceed the limit
      const totalAfterAdd = selectedIndices.length + prev.length + 1;
      if (isFreemium && totalAfterAdd > maxItems) {
        return prev;
      }
      return [...prev, { path, type, name }];
    });
  };

  const handleExpand = (path: string) => {
    setExpandedItems((prev) => (prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]));
  };

  const handleGroupToggle = (group: "public" | "private") => {
    setExpandedGroup((prev) => (prev === group ? null : group));
  };

  const handleSubmit = () => {
    const items: IPortfolioConstructionCorrelationItem[] = [];

    // Add selected indices
    selectedIndices.forEach((index) => {
      items.push({ index });
    });

    // Add selected filters
    selectedFilters.forEach((filter) => {
      if (filter.type === "asset_class") {
        items.push({ filter: { "Asset Class": [filter.name] } });
      } else if (filter.type === "sub_asset_class") {
        items.push({ filter: { "Sub Asset Class": [filter.name] } });
      } else if (filter.type === "deal") {
        items.push({ filter: { deal_name: [filter.name] } });
      }
    });

    onSubmit({ dateRange: settings.dateRange, items });
  };

  const totalSelected = selectedIndices.length + selectedFilters.length;
  const maxItems = isFreemium ? 5 : 10;
  const showWarning = isFreemium ? totalSelected === maxItems : totalSelected > maxItems;
  const filteredIndices = Object.entries(indexNames)
    .filter(([, value]) => value.toLowerCase().includes(searchQuery.toLowerCase()))
    .map(([key, value]) => ({ id: key, name: value }));

  useEffect(() => {
    getDealTree(investorId);
  }, [investorId]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <Stack sx={{ height: "100%", maxHeight: "80vh", maxWidth: "90vw" }}>
      {/* Header */}
      <Stack
        sx={{
          p: 3,
          borderBottom: "1px solid #DBE8F5",
          flexShrink: 0
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px" }}>
            Customize view
          </Typography>
          <IconButton onClick={onCancel} size="sm">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" sx={{ color: "#496682", fontSize: "14px" }}>
          Select assets, sub assets, items or combine them into the groups.
        </Typography>
      </Stack>

      {/* Body */}
      <Stack
        sx={{
          flex: 1,
          overflow: "hidden",
          height: "60vh",
          p: 3
        }}
        gap="20px"
      >
        {/* Search - Static */}
        <Box sx={{ flexShrink: 0 }}>
          <Search placeholder="Search" value={searchQuery} onChange={handleSearchChange} />
        </Box>

        {/* Public Indices */}
        <Box sx={{ flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              py: 1,
              px: 2,
              bgcolor: "#f8f9fa",
              borderRadius: "6px",
              cursor: "pointer"
            }}
            onClick={() => handleGroupToggle("public")}
          >
            <Typography variant="subtitle2">PUBLIC</Typography>
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography variant="body2" sx={{ color: "#92A7BC" }}>
                Selected ({selectedIndices.length})
              </Typography>
              <IconButton size="sm">
                <ExpandMoreIcon
                  sx={{
                    fontSize: "18px",
                    transform: expandedGroup === "public" ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s"
                  }}
                />
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={expandedGroup === "public"}>
            <Box sx={{ mt: 1, maxHeight: "200px", overflow: "auto" }}>
              {filteredIndices.map((index) => (
                <FormControlLabel
                  key={index.id}
                  control={
                    <Checkbox
                      checked={selectedIndices.includes(index.id)}
                      onChange={() => handleIndexToggle(index.id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{index.name}</Typography>}
                  sx={{ display: "flex", width: "100%", m: 0, py: 0.5 }}
                />
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Private Filters */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              py: 1,
              px: 2,
              bgcolor: "#f8f9fa",
              borderRadius: "6px",
              cursor: "pointer",
              flexShrink: 0
            }}
            onClick={() => handleGroupToggle("private")}
          >
            <Typography variant="subtitle2">PRIVATE</Typography>
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography variant="body2" sx={{ color: "#92A7BC" }}>
                Selected ({selectedFilters.length})
              </Typography>
              <IconButton size="sm">
                <ExpandMoreIcon
                  sx={{
                    fontSize: "18px",
                    transform: expandedGroup === "private" ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s"
                  }}
                />
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={expandedGroup === "private"}>
            <Box sx={{ mt: 1, height: "100%", overflow: "auto" }}>
              {dealTreeLoading && (
                <Typography variant="body2" sx={{ color: "#666", py: 2, textAlign: "center" }}>
                  Loading...
                </Typography>
              )}
              {!dealTreeLoading &&
                filteredTree &&
                filteredTree.map((assetClass) => (
                  <TreeItemCheckbox
                    key={assetClass.id}
                    item={assetClass}
                    level={0}
                    parentPath=""
                    selectedItems={selectedFilters}
                    onToggle={handleFilterToggle}
                    expandedItems={expandedItems}
                    onExpand={handleExpand}
                  />
                ))}
              {!dealTreeLoading && !filteredTree && (
                <Typography variant="body2" sx={{ color: "#666", py: 2, textAlign: "center" }}>
                  No data available
                </Typography>
              )}
              {!dealTreeLoading && filteredTree && filteredTree.length === 0 && searchQuery.trim() && (
                <Typography variant="body2" sx={{ color: "#666", py: 2, textAlign: "center" }}>
                  No matching items found
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </Stack>

      {/* Footer */}
      <Stack
        sx={{
          p: 3,
          borderTop: "1px solid #DBE8F5",
          flexShrink: 0
        }}
        gap="16px"
      >
        {/* Selection Summary */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: showWarning ? "#D93E3E" : "#496682" }}>
            {totalSelected} items selected
            {showWarning && (
              <MaterialTooltip isShow placement="right" title={`Matrix can contain up to ${maxItems} items`}>
                <ErrorOutlineIcon sx={{ fontSize: "16px", ml: 1, verticalAlign: "middle" }} />
              </MaterialTooltip>
            )}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" gap="12px" justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} sx={{ minWidth: "80px" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={totalSelected < 2} sx={{ minWidth: "80px" }}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
